import mixpanel from 'mixpanel-browser';
import type { User } from '../types/auth.type';

const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN;

let isInitialized = false;

export const initMixpanel = () => {
  if (isInitialized) return;
  if (!MIXPANEL_TOKEN) {
    console.warn('Mixpanel token is not defined in .env (REACT_APP_MIXPANEL_TOKEN)');
    return;
  }

  mixpanel.init(MIXPANEL_TOKEN, {
    debug: process.env.NODE_ENV !== 'production',
    persistence: 'localStorage',
    // Project uses EU data residency — events must be sent to the EU ingestion host.
    api_host: 'https://api-eu.mixpanel.com',
    // Page views are tracked manually via dedicated events, not automatically.
    track_pageview: false,
  });

  // Attached to every event so web and mobile can share one Mixpanel project.
  mixpanel.register({ platform: 'web' });

  isInitialized = true;
};

const isReady = () => isInitialized && !!MIXPANEL_TOKEN;

/**
 * Generic event tracker. All typed event helpers in `events.ts` route through this.
 * No-ops safely when Mixpanel is not configured.
 */
export const track = (event: string, properties?: Record<string, unknown>) => {
  if (!isReady()) return;
  try {
    mixpanel.track(event, properties);
  } catch (error) {
    console.error('Mixpanel track failed:', error);
  }
};

/**
 * Links the current (anonymous) activity to an authenticated user and sets
 * their profile properties. Call after login / signup / session restore.
 */
export const identifyUser = (user: User) => {
  if (!isReady() || !user?.id) return;
  try {
    mixpanel.identify(user.id);
    mixpanel.people.set({
      $email: user.email,
      $name: user.name,
      role: user.data?.role,
      is_premium: user.organization?.service_status
        ? user.organization.service_status === 'premium'
        : (user.organization?.is_premium ?? false),
      service_status: user.organization?.service_status ?? (user.organization?.is_premium ? 'premium' : 'free'),
      organizations_count: user.data?.access?.length ?? 0,
    });
  } catch (error) {
    console.error('Mixpanel identify failed:', error);
  }
};

/**
 * Clears the identity on logout so a later user on the same browser is not
 * merged into the previous user's profile.
 */
export const resetUser = () => {
  if (!isReady()) return;
  try {
    mixpanel.reset();
  } catch (error) {
    console.error('Mixpanel reset failed:', error);
  }
};
